import { useEffect } from 'react';
import { useFormik } from 'formik';
import * as yup from 'yup';
import DatePicker from '@/trio-ui/DatePicker';
import DropDown from '@/trio-ui/Dropdown';
import { ContractStatus, ContractType } from '@/types/api/staffMember';
import { contractTypeOptions, contractStatusOptions } from '@/utils/staffMember';
import { Grid, InputWrapper } from './StaffMemberContractForm.styles';
import { REQUIRED_FIELD_MESSAGE } from '../TechnicalValidationReview/TechnicalValidationReview.component';

const formSchema = yup.object({
  hireDate: yup.date().required(REQUIRED_FIELD_MESSAGE),
  type: yup.mixed<ContractType>().oneOf(contractTypeOptions).required(REQUIRED_FIELD_MESSAGE),
  status: yup.mixed<ContractStatus>().oneOf(contractStatusOptions).required(REQUIRED_FIELD_MESSAGE),
});

export type FormData = {
  hireDate: Date;
  type: ContractType;
  status: ContractStatus;
};

type Props = {
  hireDate?: Date;
  type?: ContractType;
  status?: ContractStatus;
  onSubmit?: (data: FormData) => void;
};

const StaffMemberContractForm = ({ hireDate, type, status, onSubmit }: Props): JSX.Element => {
  const { values, handleChange, setFieldValue, handleSubmit, submitForm, touched, errors } =
    useFormik<yup.TypeOf<typeof formSchema>>({
      initialValues: {
        hireDate,
        type,
        status,
      },
      validateOnBlur: false,
      validationSchema: formSchema,
      onSubmit: (data) => {
        if (data.hireDate && data.type && data.status) {
          onSubmit?.({
            hireDate: data.hireDate,
            type: data.type,
            status: data.status,
          });
        }
      },
    });

  useEffect(() => {
    setFieldValue('hireDate', hireDate);
    setFieldValue('type', type);
    setFieldValue('status', status);
  }, [hireDate, type, status, setFieldValue]);

  const handleChangeDropdown = (event: any): void => {
    handleChange(event);
    setTimeout(() => {
      submitForm();
    }, 100);
  };

  return (
    <form onSubmit={handleSubmit}>
      <Grid>
        <InputWrapper>
          <DropDown
            label="Contract type"
            options={contractTypeOptions}
            name="type"
            defaultValue={values.type}
            errorMessage={(touched.type && errors.type) || undefined}
            onChangeDropDown={handleChangeDropdown}
          />
        </InputWrapper>
        <InputWrapper>
          <DatePicker
            name="hireDate"
            dateFormat="MM/dd/yyyy"
            maxDate={new Date()}
            label="Hire Date"
            value={values.hireDate}
            onChange={(date) => {
              setFieldValue('hireDate', date);
              setTimeout(() => {
                submitForm();
              }, 100);
            }}
            errorMessage={(touched.hireDate && errors.hireDate) || undefined}
          />
        </InputWrapper>
        <InputWrapper>
          <DropDown
            label="Contract status"
            options={contractStatusOptions}
            name="status"
            defaultValue={values.status}
            errorMessage={(touched.status && errors.status) || undefined}
            onChangeDropDown={handleChangeDropdown}
          />
        </InputWrapper>
      </Grid>
      <button className="tw-hidden" type="submit">
        submit
      </button>
    </form>
  );
};

export default StaffMemberContractForm;
